// client/pages/OperatorsDashboard/Payouts.tsx
// KX-22 (model A) — the operator's payouts. The withdrawable balance is DERIVED
// from the ledger (the backend owns every calculation); the UI sends integer
// minor units + the collection currency and never computes a money figure.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Wallet, Loader2, AlertCircle, Check, RefreshCw } from "lucide-react";
import {
  getPayoutBalance, getPayoutBreakdown, requestPayout, listPayouts, Payout, PayoutBreakdown,
} from "../../api/services/operatorPayoutService";

const fld =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

// The operator's collection currency + its exponent. Kept as constants for the
// MVP beta; the backend is the authority on the split and the balance.
const CURRENCY = "NGN";
const FRACTION_DIGITS = 2;

export default function OperatorPayouts() {
  const { t } = useTranslation();
  const [balanceMinor, setBalanceMinor] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<PayoutBreakdown | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [amountMajor, setAmountMajor] = useState("");
  const [method, setMethod] = useState<"mobile_money" | "bank_transfer">("mobile_money");
  const [details, setDetails] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [bal, bd, list] = await Promise.all([
        getPayoutBalance(CURRENCY), getPayoutBreakdown(CURRENCY), listPayouts(),
      ]);
      setBalanceMinor(bal ? bal.balanceMinor : "0");
      setBreakdown(bd);
      setPayouts(list);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("operator.payouts.errors.load", { defaultValue: "Failed to load payouts" }));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (minor: string | null) => {
    if (minor == null) return "—";
    const n = Number(minor) / Math.pow(10, FRACTION_DIGITS);
    try {
      return new Intl.NumberFormat("en-NG", {
        style: "currency", currency: CURRENCY, minimumFractionDigits: FRACTION_DIGITS, maximumFractionDigits: FRACTION_DIGITS,
      }).format(n);
    } catch {
      return `${CURRENCY} ${n.toFixed(FRACTION_DIGITS)}`;
    }
  };

  const submit = async () => {
    setErr("");
    setMsg("");
    const major = Number(amountMajor);
    if (!major || major <= 0) {
      setErr(t("operator.payouts.errors.amount", { defaultValue: "Enter a valid amount" }));
      return;
    }
    // Unit conversion only (major -> minor); the backend owns every money figure.
    const amountMinor = String(Math.round(major * Math.pow(10, FRACTION_DIGITS)));
    setBusy(true);
    try {
      const r = await requestPayout({
        amountMinor, currency: CURRENCY, method,
        details: details ? { account: details } : {},
      });
      if (!r || r.success === false) {
        setErr(r?.message || t("operator.payouts.errors.request", { defaultValue: "Failed to request payout" }));
        return;
      }
      setMsg(t("operator.payouts.requested", { defaultValue: "Payout requested. An admin will review it." }));
      setAmountMajor("");
      setDetails("");
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("operator.payouts.errors.request", { defaultValue: "Failed to request payout" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold">{t("operator.payouts.title", { defaultValue: "Payouts" })}</h1>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className="w-4 h-4" /> {t("operator.payouts.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}
      {msg && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 px-4 py-3 text-sm">
          <Check className="w-4 h-4" /> {msg}
        </div>
      )}

      <Card className="p-5 mb-6">
        <div className="text-sm text-gray-500">{t("operator.payouts.available", { defaultValue: "Available to withdraw" })}</div>
        <div className="text-3xl font-bold mt-1">
          {loading ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> : fmt(balanceMinor)}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {t("operator.payouts.derivedNote", { defaultValue: "Derived from your ledger earnings — never a stored balance." })}
        </div>
        {breakdown && !loading && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t("operator.payouts.breakdown.acquisition", { defaultValue: "Acquisition credits" })}</span>
              <span className="font-medium">{fmt(breakdown.byAccount.acquisitionMinor)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("operator.payouts.breakdown.servicePool", { defaultValue: "Service pool share" })}</span>
              <span className="font-medium">{fmt(breakdown.byAccount.servicePoolMinor)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("operator.payouts.breakdown.standby", { defaultValue: "Standby credits" })}</span>
              <span className="font-medium">{fmt(breakdown.byAccount.standbyMinor)}</span>
            </div>
            {breakdown.byAccount.payoutMinor !== "0" && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t("operator.payouts.breakdown.paidOut", { defaultValue: "Already paid out" })}</span>
                <span className="font-medium">− {fmt(breakdown.byAccount.payoutMinor)}</span>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5 mb-6">
        <h2 className="text-sm font-semibold mb-2">{t("operator.payouts.howYouEarn.title", { defaultValue: "How you earn" })}</h2>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc pl-5">
          <li>{t("operator.payouts.howYouEarn.acquisition", { defaultValue: "A pass sold at one of your nodes earns an acquisition credit." })}</li>
          <li>{t("operator.payouts.howYouEarn.servicePool", { defaultValue: "The traffic your nodes serve earns a share of the service pool, split across nodes by GB served." })}</li>
          <li>{t("operator.payouts.howYouEarn.standby", { defaultValue: "Days with no usage earn a standby credit." })}</li>
          <li>{t("operator.payouts.howYouEarn.pending", { defaultValue: "The service-pool share is allocated monthly — it shows as pending until the allocation runs." })}</li>
        </ul>
      </Card>

      {breakdown && breakdown.byNode.length > 0 && (
        <Card className="p-0 overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 text-sm font-semibold">
            {t("operator.payouts.byNode.title", { defaultValue: "By node" })}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100 dark:border-gray-800">
                <th className="py-2 px-5">{t("operator.payouts.byNode.node", { defaultValue: "Node" })}</th>
                <th className="py-2 px-5 text-right">{t("operator.payouts.breakdown.acquisition", { defaultValue: "Acquisition credits" })}</th>
                <th className="py-2 px-5 text-right">{t("operator.payouts.breakdown.servicePool", { defaultValue: "Service pool share" })}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.byNode.map((n) => (
                <tr key={n.hotspotId} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <td className="py-2 px-5 font-medium">{n.name || "—"}</td>
                  <td className="py-2 px-5 text-right font-mono">{fmt(n.acquisitionMinor)}</td>
                  <td className="py-2 px-5 text-right font-mono">{fmt(n.servicePoolMinor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="p-5 mb-6">
        <h2 className="text-sm font-semibold mb-3">{t("operator.payouts.requestTitle", { defaultValue: "Request a payout" })}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("operator.payouts.amount", { defaultValue: `Amount (${CURRENCY})` })}</label>
            <input type="number" min={0} step="0.01" className={fld} value={amountMajor} onChange={(e) => setAmountMajor(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("operator.payouts.method", { defaultValue: "Method" })}</label>
            <select className={fld} value={method} onChange={(e) => setMethod(e.target.value as any)}>
              <option value="mobile_money">{t("operator.payouts.mobileMoney", { defaultValue: "Mobile money" })}</option>
              <option value="bank_transfer">{t("operator.payouts.bankTransfer", { defaultValue: "Bank transfer" })}</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("operator.payouts.details", { defaultValue: "Account / phone" })}</label>
            <input className={fld} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="+234… / 0123456789" />
          </div>
        </div>
        <button
          onClick={submit}
          disabled={busy}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
          {t("operator.payouts.request", { defaultValue: "Request payout" })}
        </button>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 text-sm font-semibold">
          {t("operator.payouts.history", { defaultValue: "Payout history" })}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
        ) : payouts.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm">{t("operator.payouts.empty", { defaultValue: "No payouts yet." })}</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <td className="py-3 px-5 font-medium">{fmt(p.amountMinor)}</td>
                  <td className="py-3 px-5 text-gray-500">{p.method}</td>
                  <td className="py-3 px-5 text-right">
                    <span className="inline-block rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium">{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
